// Ultimate Card Game (UCG) - Mobile Performance Tiers
import { setParticleBudget } from './particles.js';
//
// Three quality presets (see MOBILE_PLAN.md section 9), auto-selected from
// a rolling median frame time and overridable via the setup sheet's quality
// selector. Everything this touches is purely cosmetic (bloom, film grain,
// scanlines, weather, a couple of shadowBlur glows, particle count, device
// pixel ratio) - nothing here is read by the simulation, so two peers in an
// online match can safely run different tiers without desyncing.
const TIERS_DESKTOP = {
  ultra: { particles: 900, dprCap: 1.5 },
  high: { particles: 600, dprCap: 1.35 },
  medium: { particles: 350, dprCap: 1.15 },
  low: { particles: 150, dprCap: 1.0 }
};

// Throttled DPR and particle budgets for mobile / touch devices (iPhone, Android)
// to prevent GPU fillrate and bandwidth saturation on 2x/3x Retina screens.
const TIERS_MOBILE = {
  ultra: { particles: 500, dprCap: 1.25 },
  high: { particles: 350, dprCap: 1.15 },
  medium: { particles: 200, dprCap: 1.0 },
  low: { particles: 100, dprCap: 0.85 }
};

// ms/frame thresholds for auto-switching based on pure render work time (v3 §3 P1a):
// Downgrade if p75 work time > 11ms (budget at 60fps = 16.67ms) or dropped frame ratio > 8%
const WORK_DOWNGRADE_P75_MS = 11.0;
// Upgrade if p95 work time < 5.5ms sustained over 12 seconds with >= 6 active units
const WORK_UPGRADE_P95_MS = 5.5;
const UPGRADE_HEADROOM_REQUIRED_SEC = 12.0;
const DOWNGRADE_COOLDOWN_MS = 60000; // 60s cooldown to prevent flapping / DPR reallocations

// Legacy thresholds for when workMs is not supplied (test suites or callers passing only frameDtSec)
const DOWNGRADE_MS = 30;
const UPGRADE_MS = 14;
const SAMPLE_WINDOW = 90; // ~1.5s at 60fps
const MIN_SAMPLES = 60;
const SWITCH_COOLDOWN_MS = 4000;

// Manual quality choice persistence - local for now, see setManual() below
// for the profile-info hook this is meant to grow into later.
const MANUAL_TIER_STORAGE_KEY = 'ucg_quality_manual';
const PACER_FIX_MIGRATION_KEY = 'ucg_pacer_fix_v1';
const VALID_TIERS = new Set(['ultra', 'high', 'medium', 'low']);

export function checkIsMobile() {
  const ua = typeof navigator !== 'undefined' ? (navigator.userAgent || '') : '';
  return typeof window !== 'undefined' && (
    (window.matchMedia && (window.matchMedia('(max-width: 900px)').matches || window.matchMedia('(pointer: coarse)').matches)) ||
    /Mobi|Android|iPhone|iPad|iPod/i.test(ua)
  );
}

export class PerformanceManager {
  constructor(game) {
    this.game = game;
    this.isMobile = checkIsMobile();
    // Default tier: 'medium' on mobile devices, 'high' on desktop - used as
    // the starting point before enough frame samples exist to judge real
    // performance.
    const defaultTier = this.isMobile ? 'medium' : 'high';
    this.tier = defaultTier;
    // Default to auto-tuning (matches the "Graphics: Auto" default option in
    // the settings dropdown, index.html), unless the player already made an
    // explicit choice on this device - see setManual().
    this.manual = null;
    if (typeof localStorage !== 'undefined') {
      try {
        // Migration: Reset forced 'low' setting from historical frame-pacer false downgrades
        if (!localStorage.getItem(PACER_FIX_MIGRATION_KEY)) {
          if (localStorage.getItem(MANUAL_TIER_STORAGE_KEY) === 'low') {
            localStorage.removeItem(MANUAL_TIER_STORAGE_KEY);
          }
          localStorage.setItem(PACER_FIX_MIGRATION_KEY, '1');
        }

        const stored = localStorage.getItem(MANUAL_TIER_STORAGE_KEY);
        if (VALID_TIERS.has(stored)) this.manual = stored;
      } catch (_) {}
    }
    this._frameTimesMs = [];
    this._workTimesMs = [];
    this._droppedFrames = [];
    this._healthyHeadroomDuration = 0;
    this._upgradeBlockedUntil = 0;
    this._lastStageTimings = null;
    this._lastWorkMs = 0;
    this._lastSwitchAt = performance.now() + 3500; // Warmup delay on load
    // The medium->low auto-downgrade is suggested via HUD.showLowGraphicsHint()
    // instead of applied outright (see recordFrame() below) - shown at most
    // once per page load so it nudges without nagging.
    this._lowHintShown = false;

    // Telemetry / Performance HUD Overlay state
    this.overlayEnabled = false;
    if (typeof localStorage !== 'undefined') {
      try {
        this.overlayEnabled = localStorage.getItem('ucg_perf_overlay') === 'true';
      } catch (_) {}
    }
    this._lastOverlayUpdate = 0;
    this._fpsSmoothed = 60.0;
    this._domRefs = null;

    this._apply(this.manual || defaultTier);
    if (this.overlayEnabled) {
      this._syncOverlayVisibility();
    }
  }

  setManual(tier) {
    this.manual = tier;
    this._frameTimesMs.length = 0;
    if (tier) this._apply(tier);
    this.game?.hud?.clearPerfMenuNotice?.();
    // Persisted locally for now; once player profiles carry settings
    // server-side this is the write to move there instead (see
    // CLAUDE.md's Server-Autoritaet note - this is cosmetic-only, so a
    // local fallback until then is harmless).
    if (typeof localStorage !== 'undefined') {
      try {
        if (tier) localStorage.setItem(MANUAL_TIER_STORAGE_KEY, tier);
        else localStorage.removeItem(MANUAL_TIER_STORAGE_KEY);
      } catch (_) {}
    }
  }

  isOverlayEnabled() {
    return this.overlayEnabled;
  }

  setOverlay(enabled) {
    this.overlayEnabled = !!enabled;
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem('ucg_perf_overlay', String(this.overlayEnabled));
      } catch (_) {}
    }
    this._syncOverlayVisibility();
    return this.overlayEnabled;
  }

  toggleOverlay() {
    return this.setOverlay(!this.overlayEnabled);
  }

  _getDomRefs() {
    if (this._domRefs) return this._domRefs;
    if (typeof document === 'undefined') return null;

    this._domRefs = {
      overlay: document.getElementById('perf-overlay'),
      tier: document.getElementById('perf-tier'),
      fps: document.getElementById('perf-fps'),
      frameTime: document.getElementById('perf-frame-time'),
      workTime: document.getElementById('perf-work-time'),
      stages: document.getElementById('perf-stages'),
      units: document.getElementById('perf-units'),
      particles: document.getElementById('perf-particles'),
      dpr: document.getElementById('perf-dpr'),
      heap: document.getElementById('perf-heap'),
      btn: document.getElementById('btn-toggle-perf-overlay'),
      net: document.getElementById('perf-net'),
      netMode: document.getElementById('perf-net-mode'),
      netLockstep: document.getElementById('perf-net-lockstep'),
      netSync: document.getElementById('perf-net-sync'),
      netLogBtn: document.getElementById('btn-copy-net-log')
    };

    // Wired once, here rather than in the constructor, because this is the
    // first point at which the element is guaranteed to exist and the refs
    // are cached from here on.
    if (this._domRefs.netLogBtn) {
      this._domRefs.netLogBtn.addEventListener('click', () => this._copyNetLog());
    }
    return this._domRefs;
  }

  // Used to hand the netcode flight recorder's last 60 turns to the
  // clipboard via TurnScheduler.dumpFlightLog() (js/net/turnScheduler.js),
  // removed along with the rest of the dead WebRTC/PHP lockstep stack (see
  // docs/SCALING_AUDIT_KRITERIENKATALOG.md K-1.4). this.game.turnScheduler
  // is always null now, so the button below always reports "no match
  // active" - see K-3.3 for wiring an equivalent net-stats source for the
  // WebSocket architecture.
  _copyNetLog() {
    const dom = this._domRefs;
    const btn = dom?.netLogBtn;
    if (!btn) return;
    const label = btn.dataset.label || btn.textContent;
    const scheduler = this.game.turnScheduler;
    if (scheduler?.dumpFlightLog) {
      const log = scheduler.dumpFlightLog();
      btn.textContent = log?.history?.length
        ? `✅ ${log.history.length} Turns kopiert`
        : '✅ Log in der Konsole';
    } else if (this.game.isOnline && this.game.wsClient && this.game.stateStream) {
      const stream = this.game.stateStream;
      const stats = stream.getNetStats?.() || {};
      const intervals = stream._snapshotIntervalsMs || [];
      const netLog = {
        timestamp: new Date().toISOString(),
        mode: 'WebSocket (Oracle Cloud Always-Free)',
        server: this.game.wsClient?.serverUrl || 'wss://game.zernecke.de',
        team: this.game.myTeam,
        roomId: this.game.wsClient?._reconnectInfo?.roomId || null,
        pingMs: stats.pingMs,
        snapshotHz: stats.snapshotHz ? +stats.snapshotHz.toFixed(1) : null,
        jitterMs: stats.jitterMs ? +stats.jitterMs.toFixed(1) : null,
        peakJitterMs: stats.peakJitterMs ? +stats.peakJitterMs.toFixed(1) : null,
        lastReceivedSeq: stream.lastReceivedSeq,
        activeUnitsCount: this.game.battlefield?.units?.length || 0,
        recentIntervalsMs: intervals.slice(-30).map(v => Math.round(v * 10) / 10)
      };

      const logStr = JSON.stringify(netLog, null, 2);
      console.log('[UCG Net-Log]', netLog);
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(logStr).catch(() => {});
      }
      btn.textContent = '✅ Net-Log kopiert';
    } else {
      btn.textContent = '⚠️ Kein Match aktiv';
    }

    clearTimeout(this._netLogBtnTimer);
    this._netLogBtnTimer = setTimeout(() => {
      btn.textContent = btn.dataset.label || label;
    }, 1600);
  }

  _syncOverlayVisibility() {
    const dom = this._getDomRefs();
    if (!dom) return;

    if (dom.overlay) {
      dom.overlay.classList.toggle('hidden', !this.overlayEnabled);
    }
    if (dom.btn) {
      dom.btn.innerText = this.overlayEnabled ? 'Perf HUD: ON' : 'Perf HUD: OFF';
      dom.btn.classList.toggle('active', this.overlayEnabled);
    }
  }

  // frameDtSec: real (wall-clock) frame delta from the game loop, in seconds
  // workMs: pure rendering work time measured via performance.now() (v3 §3 P1a)
  // stageTimings: detailed breakdown { worldMs, postMs, hudMs, simMs }
  recordFrame(frameDtSec, workMs = null, stageTimings = null) {
    if (stageTimings) {
      this._lastStageTimings = stageTimings;
    }
    if (typeof workMs === 'number' && Number.isFinite(workMs)) {
      this._lastWorkMs = workMs;
    }

    const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();

    // 1. Auto-tuning sampling
    if (!this.manual) {
      if (workMs !== null && workMs !== undefined) {
        // VSync drop tracker: check if frameDtSec exceeded 1.5x of target frame duration
        // (a paused scene is deliberately drawn at 15 fps, see Game.gameLoop)
        const targetDt = this.game?.isEcoMode ? 0.0333 : (this.game?.isPaused ? 0.0666 : 0.0166);
        const isDropped = frameDtSec > (targetDt * 1.5);
        this._droppedFrames.push(isDropped ? 1 : 0);
        if (this._droppedFrames.length > SAMPLE_WINDOW) this._droppedFrames.shift();

        // Modern work-time based auto-tuning (v3 §3 P1a)
        // On mobile GPUs (tile-based deferred renderers), Canvas 2D command buffer flush happens
        // asynchronously outside JS performance.now() timing. When frameDtSec shows a genuine
        // GPU-bound frame stall (1.35x targetDt .. 85ms, excluding tab-background pauses),
        // fold the GPU stall overrun into the recorded sample so p75/p95 reflect real thermal load.
        let effectiveWorkMs = workMs;
        if (this.isMobile && frameDtSec > targetDt * 1.35 && frameDtSec < 0.085) {
          const gpuOverrunMs = (frameDtSec - targetDt) * 1000;
          effectiveWorkMs = Math.max(workMs, Math.min(24.0, workMs + gpuOverrunMs * 0.65));
        }
        this._workTimesMs.push(effectiveWorkMs);
        if (this._workTimesMs.length > SAMPLE_WINDOW) this._workTimesMs.shift();

        this._evalCounter = (this._evalCounter || 0) + 1;
        if (this._workTimesMs.length >= MIN_SAMPLES) {
          if (this._evalCounter % 10 === 0 || this._cachedP75 === undefined) {
            const sorted = [...this._workTimesMs].sort((a, b) => a - b);
            const p75Idx = Math.floor(sorted.length * 0.75);
            const p95Idx = Math.floor(sorted.length * 0.95);
            this._cachedP75 = sorted[p75Idx];
            this._cachedP95 = sorted[p95Idx];
          }
          const p75 = this._cachedP75;
          const p95 = this._cachedP95;

          const droppedCount = this._droppedFrames.reduce((acc, v) => acc + v, 0);
          const droppedRatio = droppedCount / this._droppedFrames.length;

          const shouldDowngrade = (p75 > WORK_DOWNGRADE_P75_MS || droppedRatio > 0.08);

          if (shouldDowngrade && this.tier !== 'low' && now - this._lastSwitchAt >= SWITCH_COOLDOWN_MS) {
            if (this.tier === 'medium') {
              this._apply('low');
              this._notifyLowGraphicsSuggested();
              this.game?.hud?.showPerfMenuNotice?.();
            } else {
              const nextTier = this.tier === 'ultra' ? 'high' : 'medium';
              this._apply(nextTier);
            }
            this._lastSwitchAt = now;
            this._upgradeBlockedUntil = now + DOWNGRADE_COOLDOWN_MS;
            this._healthyHeadroomDuration = 0;
            this._workTimesMs.length = 0;
            this._droppedFrames.length = 0;
            this._cachedP75 = undefined;
            this._cachedP95 = undefined;
          } else if (p95 < WORK_UPGRADE_P95_MS && this.tier !== 'ultra') {
            this._healthyHeadroomDuration += frameDtSec;
            const activeUnits = this.game?.battlefield?.units?.length || 0;
            const hasWarmupProtection = activeUnits >= 6;
            const cooldownExpired = now >= this._upgradeBlockedUntil && (now - this._lastSwitchAt >= SWITCH_COOLDOWN_MS);

            if (this._healthyHeadroomDuration >= UPGRADE_HEADROOM_REQUIRED_SEC && hasWarmupProtection && cooldownExpired) {
              this.game?.hud?.clearPerfMenuNotice?.();
              if (this.tier === 'low') {
                this._apply('medium');
              } else if (this.tier === 'medium') {
                // Phones are capped at 'medium' by the auto-tuner: JS work time
                // (all this measures) says nothing about GPU/thermal cost, and
                // 'high' adds bloom + glass sheen. A cool phone looked fast,
                // auto-upgraded, heated up, throttled and dropped frames again.
                // 'high' stays available as an explicit manual choice.
                if (!this.isMobile) {
                  this._apply('high');
                }
              } else if (this.tier === 'high' && !this.isMobile && p95 < 4.0) {
                this._apply('ultra');
              }
              this._lastSwitchAt = now;
              this._healthyHeadroomDuration = 0;
              this._workTimesMs.length = 0;
              this._droppedFrames.length = 0;
              this._cachedP75 = undefined;
              this._cachedP95 = undefined;
            }
          } else {
            // Headroom lost or insufficient
            this._healthyHeadroomDuration = Math.max(0, this._healthyHeadroomDuration - frameDtSec * 2);
          }
        }
      } else {
        // Fallback: Legacy frameDtSec-based auto-tuning (backwards compatibility for tests)
        this._frameTimesMs.push(frameDtSec * 1000);
        if (this._frameTimesMs.length > SAMPLE_WINDOW) this._frameTimesMs.shift();
        if (this._frameTimesMs.length >= MIN_SAMPLES) {
          if (now - this._lastSwitchAt >= SWITCH_COOLDOWN_MS) {
            this._evalCounterLegacy = (this._evalCounterLegacy || 0) + 1;
            if (this._evalCounterLegacy % 10 === 0 || this._cachedMedian === undefined) {
              const sorted = [...this._frameTimesMs].sort((a, b) => a - b);
              this._cachedMedian = sorted[Math.floor(sorted.length / 2)];
            }
            const median = this._cachedMedian;

            if (median > DOWNGRADE_MS && this.tier !== 'low') {
              if (this.tier === 'medium') {
                this._apply('low');
                this._notifyLowGraphicsSuggested();
                this.game?.hud?.showPerfMenuNotice?.();
              } else {
                const nextTier = this.tier === 'ultra' ? 'high' : 'medium';
                this._apply(nextTier);
              }
              this._lastSwitchAt = now;
              this._frameTimesMs.length = 0;
            } else if (median < UPGRADE_MS && this.tier !== 'ultra') {
              this.game?.hud?.clearPerfMenuNotice?.();
              if (this.tier === 'low') {
                this._apply('medium');
              } else if (this.tier === 'medium') {
                if (!this.isMobile || median < 7.5) {
                  this._apply('high');
                }
              } else if (this.tier === 'high' && !this.isMobile && median < 6.5) {
                this._apply('ultra');
              }
              this._lastSwitchAt = now;
              this._frameTimesMs.length = 0;
            }
          }
        }
      }
    }

    // 2. Real-time telemetry overlay update (Throttled to ~150ms to eliminate DOM thrashing)
    this.updateOverlay(frameDtSec, now);
  }

  // Shown at most once per page load. Skipped (and left un-flagged, so it
  // can still fire later) while the setup sheet is already open - the
  // quality selector this points at is already right there on screen.
  _notifyLowGraphicsSuggested() {
    if (this._lowHintShown) return;
    const hud = this.game.hud;
    if (hud?.isSetupSheetOpen?.()) return;
    if (hud?.showLowGraphicsHint) this._lowHintShown = !!hud.showLowGraphicsHint();
  }

  updateOverlay(frameDtSec, nowMs = performance.now()) {
    // Smoothed FPS tracking (exponential moving average)
    const instantFps = 1.0 / Math.max(0.0001, frameDtSec);
    this._fpsSmoothed = this._fpsSmoothed * 0.88 + instantFps * 0.12;

    if (!this.overlayEnabled) return;
    if (nowMs - this._lastOverlayUpdate < 150) return; // 150ms throttle
    this._lastOverlayUpdate = nowMs;

    const dom = this._getDomRefs();
    if (!dom || !dom.overlay) return;

    // A. FPS & Color-coding
    if (dom.fps) {
      dom.fps.textContent = this._fpsSmoothed.toFixed(1);
      dom.fps.className = 'perf-val ' + (this._fpsSmoothed >= 54 ? 'good' : (this._fpsSmoothed >= 32 ? 'warn' : 'bad'));
    }

    // B. Frame Time (ms)
    if (dom.frameTime) {
      dom.frameTime.textContent = (frameDtSec * 1000).toFixed(1) + ' ms';
    }

    // B2. Work Time (ms)
    if (dom.workTime) {
      dom.workTime.textContent = this._lastWorkMs > 0 ? (this._lastWorkMs.toFixed(1) + ' ms') : '-- ms';
    }

    // B3. Stage Timings Breakdown
    if (dom.stages && this._lastStageTimings) {
      const { worldMs = 0, postMs = 0, hudMs = 0 } = this._lastStageTimings;
      dom.stages.textContent = `W:${worldMs.toFixed(1)} P:${postMs.toFixed(1)} H:${hudMs.toFixed(1)}`;
    }

    // C. Tier Badge
    if (dom.tier) {
      dom.tier.textContent = (this.tier || 'HIGH').toUpperCase() + (this.manual ? '' : ' • AUTO');
    }

    // D. Active Units Count (Western / Eastern)
    if (dom.units) {
      let westCount = 0;
      let eastCount = 0;
      const units = this.game.battlefield?.units;
      if (Array.isArray(units)) {
        for (let i = 0; i < units.length; i++) {
          if (units[i].team === 'western') westCount++;
          else if (units[i].team === 'eastern') eastCount++;
        }
        dom.units.textContent = `${units.length} (W:${westCount} E:${eastCount})`;
      } else {
        dom.units.textContent = '0';
      }
    }

    // E. Particles Count & Budget
    if (dom.particles) {
      const pSys = this.game.particles;
      const activeP = pSys ? (typeof pSys.getActiveCount === 'function' ? pSys.getActiveCount() : (pSys.particles?.length || 0)) : 0;
      const tierMap = this.isMobile ? TIERS_MOBILE : TIERS_DESKTOP;
      const budget = (tierMap[this.tier] || tierMap.medium).particles;
      dom.particles.textContent = `${activeP} / ${budget}`;
    }

    // F. DPR & Resolution
    if (dom.dpr) {
      const vp = this.game.viewport;
      const dpr = vp?.dpr || (typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);
      const w = this.game.renderer?.width || 1600;
      const h = this.game.renderer?.height || 540;
      dom.dpr.textContent = `${dpr.toFixed(1)}x (${w}x${h})`;
    }

    // G. JS Heap (Chrome/Chromium performance.memory API)
    if (dom.heap) {
      const mem = typeof performance !== 'undefined' ? performance.memory : null;
      if (mem && typeof mem.usedJSHeapSize === 'number') {
        const heapMb = (mem.usedJSHeapSize / (1024 * 1024)).toFixed(1);
        dom.heap.textContent = `${heapMb} MB`;
      } else {
        dom.heap.textContent = 'N/A';
      }
    }

    // H. Online telemetry - hidden outright in a local game, since none of
    // it means anything there. See TurnScheduler.getNetStats() (legacy,
    // always null now - js/net/turnScheduler.js was removed with the dead
    // WebRTC/PHP lockstep stack, K-1.4) and StateStreamManager.getNetStats()
    // (current WebSocket architecture, K-3.3).
    this._updateNetOverlay(dom);
  }

  _updateNetOverlay(dom) {
    if (!dom.net) return;

    // Legacy lockstep path - kept so this method (and the tests exercising
    // it, tests/net_hud_test.mjs) still work exactly as before if a real
    // TurnScheduler is ever wired back up; in production today
    // game.turnScheduler is always null, so this branch never actually
    // fires - see the WS branch below for what real online matches show.
    const scheduler = this.game.isOnline ? this.game.turnScheduler : null;
    const legacyStats = scheduler?.getNetStats ? scheduler.getNetStats() : null;
    if (legacyStats) {
      dom.net.classList.remove('hidden');
      this._renderLegacyNetStats(dom, legacyStats);
      return;
    }

    // Current WebSocket architecture (server/matchRoom.js + K-3.3).
    const stream = this.game.isOnline && this.game.wsClient ? this.game.stateStream : null;
    const streamStats = stream?.getNetStats ? stream.getNetStats() : null;
    if (!streamStats) {
      dom.net.classList.add('hidden');
      return;
    }
    dom.net.classList.remove('hidden');
    this._renderStreamNetStats(dom, streamStats);
  }

  _renderLegacyNetStats(dom, stats) {
    // Transport. A direct DataChannel is the good case; the HTTP polling
    // fallback still plays, it just costs latency - hence warn, not bad.
    if (dom.netMode) {
      dom.netMode.textContent = stats.p2p
        ? `P2P ${stats.pingMs} ms`
        : `HTTP ${stats.pingMs} ms`;
      dom.netMode.className = 'perf-val ' + (stats.p2p ? 'good' : 'warn');
    }

    // Lockstep barrier: turns of opponent input already buffered ahead of
    // the turn being simulated. Waiting on the barrier right now is the
    // thing that actually shows as a freeze, so it outranks the count.
    if (dom.netLockstep) {
      dom.netLockstep.textContent = stats.stalled
        ? `WAIT (${stats.stallCount})`
        : `+${stats.bufferedTurns} (${stats.stallCount} stalls)`;
      dom.netLockstep.className = 'perf-val ' +
        (stats.stalled ? 'bad' : (stats.bufferedTurns > 0 ? 'good' : 'warn'));
    }

    // Hash agreement. Any mismatch at all is worth shouting about, and the
    // turn it happened on is what makes it findable in the flight log.
    if (dom.netSync) {
      dom.netSync.textContent = stats.desyncCount === 0
        ? 'bit-exact'
        : `${stats.desyncCount}x @T${stats.lastDesyncTurn}`;
      dom.netSync.className = 'perf-val ' + (stats.desyncCount === 0 ? 'good' : 'bad');
    }
  }

  // Same 3 DOM slots as _renderLegacyNetStats(), reinterpreted for
  // server-authoritative state streaming: there is no lockstep buffer or
  // CRC desync count to show here at all (the client never runs its own
  // simulation to diverge from the server's) - what actually matters for
  // "does this connection feel good right now" is round-trip latency and
  // how evenly the 30 Hz snapshot stream is actually arriving.
  _renderStreamNetStats(dom, stats) {
    if (dom.netMode) {
      const known = typeof stats.pingMs === 'number';
      dom.netMode.textContent = known ? `WS ${Math.round(stats.pingMs)} ms` : 'WS -- ms';
      dom.netMode.className = 'perf-val ' + (known && stats.pingMs > 150 ? 'warn' : 'good');
    }

    if (dom.netLockstep) {
      dom.netLockstep.textContent = `${stats.snapshotHz.toFixed(1)} Hz`;
      // Nominal rate is 30 Hz (server/matchRoom.js) - a couple Hz of slack
      // for normal scheduling jitter before calling it degraded.
      dom.netLockstep.className = 'perf-val ' + (stats.snapshotHz >= 27 ? 'good' : 'warn');
    }

    if (dom.netSync) {
      dom.netSync.textContent = `${stats.jitterMs.toFixed(1)} ms jitter`;
      dom.netSync.className = 'perf-val ' + (stats.jitterMs > 40 ? 'bad' : (stats.jitterMs > 15 ? 'warn' : 'good'));
    }
  }

  _apply(tier) {
    this.tier = tier;
    const tierMap = this.isMobile ? TIERS_MOBILE : TIERS_DESKTOP;
    const cfg = tierMap[tier] || tierMap.medium;
    if (this.game.renderer) {
      this.game.renderer.quality = tier;
      this.game.renderer.isMobile = this.isMobile;
    }
    if (this.game.particles) {
      this.game.particles.quality = tier;
      this.game.particles.isMobile = this.isMobile;
    }
    setParticleBudget(cfg.particles);
    if (this.game.viewport) {
      this.game.viewport.dprCap = cfg.dprCap;
      this.game.viewport.resize();
    }
    const scanlinesEl = typeof document !== 'undefined' ? document.getElementById('battlefield-scanlines') : null;
    if (scanlinesEl) {
      scanlinesEl.classList.toggle('hidden', tier === 'low');
    }
    if (this.overlayEnabled) {
      this.updateOverlay(0.016, performance.now() + 200);
    }
  }
}
